// Pure rules behind the Schedules simple view's row actions (Confirm, Orders,
// Messages, Tasks). Nothing here fetches, so every rule is testable on its own;
// the loading lives in hooks/useScheduleRowActionData.js.
import { normalizeStatus } from "./workItemStatus";
import { normalizePhone, rowPhonesNormalized, smsCandidates } from "./smsPhone";

const PUBLIC = "public";

// ── Patient matching ────────────────────────────────────────────────────────
// Shared with ClinicPatientOrdersModal so a row's counts and the list that
// modal opens onto can never disagree about whose orders are whose.

// Same normalization the backend's `_normalize_identity_text` applies before
// grouping, so a name that matched server-side also matches here. Strips the
// `EHR: 1234` decorations some rows carry in `patient_name`.
export function normalizeName(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/\behr\s*[:#-]?\s*\w+\b/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

// Order-insensitive name key. The chart stores "Carson, James"; the orders
// table stores whatever the recorder had ("James Carson", "Carson_James").
// Sorting the tokens collapses those forms onto one key.
export function nameKey(value) {
  return normalizeName(value).split(" ").filter(Boolean).sort().join(" ");
}

// The fetchOrders() patient groups that belong to one patient: the same EHR
// id, or the same order-insensitive name. `ehr_id` is blank on most
// audio-derived rows, so the name is usually the only join key.
export function matchPatientGroups(groups, { patientName, ehrId } = {}) {
  const wantKey = nameKey(patientName);
  const ehr = String(ehrId || "").trim();
  return (groups || []).filter((g) => {
    if (ehr && String(g?.ehr_id || "").trim() === ehr) return true;
    return Boolean(wantKey) && nameKey(g?.patient_name) === wantKey;
  });
}

// One entry per order. A row can surface from two schemas only when both
// genuinely hold a copy; key on id so it is listed once.
export function uniqueOrderItems(items) {
  const seen = new Set();
  return (items || []).filter((item) => {
    const key = item?.id || `${item?.item_type}|${item?.description}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// ── Schemas ─────────────────────────────────────────────────────────────────

// The schema a schedule row lives in. Fan-out rows carry `_schema`; anything
// else lives in the viewer's own schema.
export function rowSchemaOf(row, fallbackSchema) {
  return String(row?._schema || "").trim() || String(fallbackSchema || "").trim() || PUBLIC;
}

// Orders for a row are read from its own schema AND public: the recorder files
// a tenant patient's orders under public more often than not (see the note at
// the top of ClinicPatientOrdersModal).
export function orderSchemasForRow(row, fallbackSchema) {
  const own = rowSchemaOf(row, fallbackSchema);
  return own === PUBLIC ? [PUBLIC] : [own, PUBLIC];
}

// Every schema the rows need orders from, each once, in first-seen order.
export function orderSchemasForRows(rows, fallbackSchema) {
  const out = [];
  for (const row of rows || []) {
    for (const schema of orderSchemasForRow(row, fallbackSchema)) {
      if (!out.includes(schema)) out.push(schema);
    }
  }
  return out;
}

// ── Orders ──────────────────────────────────────────────────────────────────

// Lookup tables over one schema's patient groups, built once per load so each
// row is a couple of map reads instead of a pass over every patient on file.
export function buildOrderIndex(groups) {
  const byName = new Map();
  const byEhr = new Map();
  (groups || []).forEach((group, position) => {
    const entry = { group, position };
    const key = nameKey(group?.patient_name);
    if (key) {
      if (!byName.has(key)) byName.set(key, []);
      byName.get(key).push(entry);
    }
    const ehr = String(group?.ehr_id || "").trim();
    if (ehr) {
      if (!byEhr.has(ehr)) byEhr.set(ehr, []);
      byEhr.get(ehr).push(entry);
    }
  });
  return { byName, byEhr };
}

// matchPatientGroups() over an index: the same groups, in the same order.
export function matchIndexedGroups(index, { patientName, ehrId } = {}) {
  if (!index) return [];
  const found = new Map();
  const ehr = String(ehrId || "").trim();
  if (ehr) (index.byEhr.get(ehr) || []).forEach((e) => found.set(e.position, e.group));
  const key = nameKey(patientName);
  if (key) (index.byName.get(key) || []).forEach((e) => found.set(e.position, e.group));
  return [...found.entries()].sort((a, b) => a[0] - b[0]).map(([, group]) => group);
}

// A schedule row's orders and follow-ups, each tagged with the schema it was
// read from (writes must go back there). The row is matched the way the chart
// modal matches its patient: `patient_name` + `EHR_ID`.
export function orderItemsForRow(row, indexBySchema, fallbackSchema) {
  const who = { patientName: row?.patient_name || "", ehrId: row?.EHR_ID || "" };
  const items = [];
  for (const schema of orderSchemasForRow(row, fallbackSchema)) {
    for (const group of matchIndexedGroups(indexBySchema?.[schema], who)) {
      for (const item of group?.items || []) items.push({ ...item, _schema: item._schema || schema });
    }
  }
  return uniqueOrderItems(items);
}

const isCompleted = (item) => normalizeStatus(item?.status) === "completed";

// "Still missing" is everything not completed: the same open count the chart's
// Orders modal shows in its header.
export function summarizeOrderItems(items) {
  const total = (items || []).length;
  const completed = (items || []).filter(isCompleted).length;
  return { total, completed, missing: total - completed };
}

const stamp = (item, ...fields) => {
  for (const field of fields) if (item?.[field]) return String(item[field]);
  return "";
};

// Missing newest first; completed most recently completed first.
export function splitOrderItems(items) {
  const list = items || [];
  return {
    missing: list
      .filter((i) => !isCompleted(i))
      .sort((a, b) => stamp(b, "created_at").localeCompare(stamp(a, "created_at"))),
    completed: list
      .filter(isCompleted)
      .sort((a, b) =>
        stamp(b, "completed_at", "updated_at", "created_at").localeCompare(
          stamp(a, "completed_at", "updated_at", "created_at"),
        ),
      ),
  };
}

// ── Messages ────────────────────────────────────────────────────────────────

// 10-digit phone, the way every SMS badge in the app matches numbers
// (ResultsTable, AdminPage, PatientMessagingModal). Lives in utils/smsPhone
// with the cell/home rule; re-exported here for the existing callers.
export { normalizePhone, rowPhones, rowPhonesNormalized } from "./smsPhone";

// The row's primary number: the cell, else the home phone (utils/smsPhone
// rule 1). Anything that matches SMS rows to a patient must use rowPhones():
// a text can arrive from either of the patient's numbers.
export function rowPhone(row) {
  return smsCandidates(row)[0]?.phone || null;
}

// { [phone]: count } over unreviewed inbound SMS rows (their `from` column).
export function countUnreadByPhone(smsRows) {
  const counts = {};
  for (const sms of smsRows || []) {
    const phone = normalizePhone(sms?.from);
    if (phone) counts[phone] = (counts[phone] || 0) + 1;
  }
  return counts;
}

// Unread count for a row: the sum over every number the patient has.
export function unreadForRow(row, counts) {
  if (!counts) return 0;
  let n = 0;
  rowPhonesNormalized(row).forEach((phone) => {
    n += counts[phone] || 0;
  });
  return n;
}

// ── Tasks ───────────────────────────────────────────────────────────────────

// { [schema]: [patient name, ...] } for the rows that name a patient, each
// list sorted and deduplicated so it makes a stable lookup key. One task
// lookup per schema covers every patient on the page.
export function patientNamesBySchema(rows, fallbackSchema) {
  const sets = {};
  for (const row of rows || []) {
    const name = String(row?.patient_name || "").trim();
    if (!name) continue;
    const schema = rowSchemaOf(row, fallbackSchema);
    (sets[schema] = sets[schema] || new Set()).add(name);
  }
  const out = {};
  Object.keys(sets)
    .sort()
    .forEach((schema) => {
      out[schema] = [...sets[schema]].sort();
    });
  return out;
}

// Whether this user assigned the task or holds it: only then can the Tasks
// tab (which lists the viewer's own tasks) show it.
export function isOnTask(task, userId) {
  return Boolean(userId) && (task?.assigned_to === userId || task?.assigned_by === userId);
}

// Tasks made from this patient's charts, any visit. The clinic page stamps the
// patient's name (and the chart's schema) on a task it creates; a task with no
// schema stamp is matched on the name alone.
export function tasksForRow(row, tasks, fallbackSchema) {
  const want = nameKey(row?.patient_name);
  if (!want) return [];
  const schema = rowSchemaOf(row, fallbackSchema).toLowerCase();
  return (tasks || []).filter((task) => {
    if (nameKey(task?.source_patient_name) !== want) return false;
    const taskSchema = String(task?.source_doc_schema || "").trim().toLowerCase();
    return !taskSchema || taskSchema === schema;
  });
}

export function isOpenTask(task) {
  const status = normalizeStatus(task?.status);
  return status !== "completed" && status !== "cancelled";
}

// Open first (soonest due, then newest); closed after, newest first.
export function sortTasks(tasks) {
  const due = (t) => String(t?.due_date || "");
  return [...(tasks || [])].sort((a, b) => {
    const openA = isOpenTask(a);
    const openB = isOpenTask(b);
    if (openA !== openB) return openA ? -1 : 1;
    if (openA && due(a) !== due(b)) {
      if (!due(a)) return 1;
      if (!due(b)) return -1;
      return due(a).localeCompare(due(b));
    }
    return stamp(b, "created_at").localeCompare(stamp(a, "created_at"));
  });
}

// ── Dates ───────────────────────────────────────────────────────────────────

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2026-09-30" → "Sep 30" (this year) or "Sep 30, 2025". Reads the parts
// directly: `new Date("2026-09-30")` is UTC midnight, a day early west of UTC.
export function shortVisitDate(value, today = new Date()) {
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(String(value || "").trim());
  if (!m) return "";
  const label = `${MONTHS[parseInt(m[2], 10) - 1] || m[2]} ${parseInt(m[3], 10)}`;
  return parseInt(m[1], 10) === today.getFullYear() ? label : `${label}, ${m[1]}`;
}

// A timestamp → "9/30", in the viewer's zone (the Confirm cell's format).
export function monthDay(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : `${d.getMonth() + 1}/${d.getDate()}`;
}
